import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@mui/material";
import { IconCalendarMonth } from "@tabler/icons-react";
import { onSnapshot, query, where } from "firebase/firestore";
import { filter, groupBy, map, minBy, sumBy } from "lodash";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import EventoCarteiraDb from "../../../../../db/eventoCarteiraDb";
import NotaDb from "../../../../../db/notaDb";
import { EventoCarteira } from "../../../../../models/eventoCarteira";
import { NotaBMF, NotaBovespa } from "../../../../../models/nota";
import { useUsuario } from "../../../../../providers/usuarioProvider";
import Calendario, { CalendarioData } from "../../../../util/charts/calendario";
import FelixCard from "../../../../util/ui/felixCard";
import InteiroText from "../../../../util/ui/inputs/inteiroText";
import { TimelineMensal } from "./timelineMensal";

type Props = {
  mes: Moment;
};

export default function CardCalendarioMes({ mes }: Props) {
  const [notas, setNotas] = useState<(NotaBMF | NotaBovespa)[]>([]);
  const [eventos, setEventos] = useState<EventoCarteira[]>([]);
  const { visualizado } = useUsuario();
  const dataInicio = mes.clone().startOf("month");
  const dataFim = mes.clone().endOf("month");
  const [_diaSelecionado, setDiaSelecionado] = useState<CalendarioData>();

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new NotaDb().collection({ usuarioId: visualizado, notaId: "" }),
          where("dataPregao", ">=", dataInicio.toDate()),
          where("dataPregao", "<", dataFim.toDate())
        ),
        (query) => {
          setNotas(map(query.docs, (doc) => doc.data()));
        }
      );
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new EventoCarteiraDb().collection({
            usuarioId: visualizado,
            eventoId: "",
          }),
          where("dataEvento", ">=", dataInicio.toDate()),
          where("dataEvento", "<", dataFim.toDate())
        ),
        (query) => {
          setEventos(map(query.docs, (doc) => doc.data()));
        }
      );
  }, [visualizado]);

  const data: CalendarioData[] = map(
    groupBy(
      map(
        notas,
        (nota) =>
          ({
            day: nota.dataPregao.format("YYYY-MM-DD"),
            value: CalendarioEvento.Nota,
            nota,
          } as CalendarioData)
      ).concat(
        map(
          eventos,
          (evento) =>
            ({
              day: evento.dataEvento.format("YYYY-MM-DD"),
              value: CalendarioEvento.Evento,
              evento,
            } as CalendarioData)
        )
      ),
      "day"
    ),
    (calendarioEvento, day) => ({
      day,
      value: minBy(calendarioEvento, "value")!.value,
      aux: calendarioEvento,
    })
  );

  const onClick = (dia: CalendarioData) => {
    setDiaSelecionado(dia);
  };

  const formatTooltip = (point: any) => {
    const notas: (NotaBMF | NotaBovespa)[] = map(
      filter(
        point.aux,
        (calendarioEvento) => calendarioEvento.value === CalendarioEvento.Nota
      ),
      "nota"
    );
    const eventos: EventoCarteira[] = map(
      filter(
        point.aux,
        (calendarioEvento) => calendarioEvento.value === CalendarioEvento.Evento
      ),
      "evento"
    );

    return (
      <Card elevation={0} sx={(theme) => ({ boxShadow: theme.shadows[16] })}>
        <Box
          display="flex"
          flexDirection="column"
          padding={1.5}
          gap={0.5}
          minWidth={140}
        >
          {/* Data é dado: mono tabular. */}
          <Typography
            variant="dataMono"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            {moment.tz(point.day, "America/Sao_Paulo").format("DD/MM")}
          </Typography>
          <LinhaTooltip rotulo="Notas:" valor={notas.length} />
          <LinhaTooltip
            rotulo="Operações:"
            valor={sumBy(notas, (nota) => nota.quantidadeOperacoes)}
          />
          <LinhaTooltip rotulo="Eventos:" valor={eventos.length} />
        </Box>
      </Card>
    );
  };

  return (
    <FelixCard>
      <CardHeader title="Calendário" avatar={<IconCalendarMonth size={16} />} />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} style={{ height: 240 }}>
            <Box
              sx={{
                width: "100%",
                height: "calc(100% - 16px)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              <Calendario
                data={data}
                dataInicio={dataInicio}
                dataFim={dataFim.clone().add(1, "day")}
                direction="vertical"
                formatTooltip={formatTooltip}
                onClick={onClick}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={(theme) => ({
              height: 240,
              overflow: "auto",
              // Scrollbar na rampa de tinta — nada de cinza avulso.
              "&::-webkit-scrollbar": {
                width: 12,
              },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.ink["08"],
                backgroundClip: "padding-box !important",
                borderLeft: "4px transparent solid",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.ink[20],
                backgroundClip: "padding-box !important",
                borderLeft: "4px transparent solid",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: theme.palette.ink[40],
              },
            })}
          >
            <TimelineMensal notas={notas} eventos={eventos} />
          </Grid>
        </Grid>
      </CardContent>
    </FelixCard>
  );
}

/** Linha de tooltip do calendário: rótulo mono à esquerda, número mono tabular
 *  à direita. */
function LinhaTooltip({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" gap={2}>
      <Typography variant="labelMono" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography variant="dataMono" color="text.primary">
        <InteiroText value={valor} />
      </Typography>
    </Box>
  );
}

enum CalendarioEvento {
  Hoje = 2,
  Nota = 0,
  Evento = 1,
}

export type TimelineMensalProps = {
  notas: (NotaBMF | NotaBovespa)[];
  eventos: EventoCarteira[];
};
